import {
  FINANCE_RECURRENCE_MONTHS,
  type FinanceRecurrenceValue,
  PAGINATION,
  type PermissionLevelValue,
  type PermissionModule,
} from '../constants/index';

const LEVEL_WEIGHT: Record<PermissionLevelValue, number> = { NONE: 0, READ: 1, WRITE: 2 };

/** True se o nivel do modulo alcanca o exigido (WRITE cobre READ). */
export function hasModulePermission(
  permissions: Record<PermissionModule, PermissionLevelValue>,
  module: PermissionModule,
  required: PermissionLevelValue,
): boolean {
  return LEVEL_WEIGHT[permissions[module]] >= LEVEL_WEIGHT[required];
}

/** Formata um valor monetario no padrao pt-BR. */
export function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
}

/** Formata uma data no padrao dd/mm/aaaa. */
export function formatDate(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
}

/** Formata data + hora no padrao pt-BR. */
export function formatDateTime(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Ha quanto tempo, em uma etiqueta curta ("agora", "2 h", "5 d"). Cabe ao lado
 * de um nome em cartao de quadro, onde a data cheia nao caberia.
 */
export function formatElapsed(value: Date | string, now: Date = new Date()): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const minutos = Math.floor((now.getTime() - date.getTime()) / 60_000);

  if (minutos < 1) return 'agora';
  if (minutos < 60) return `${minutos} min`;
  if (minutos < 60 * 24) return `${Math.floor(minutos / 60)} h`;

  return `${Math.floor(minutos / (60 * 24))} d`;
}

/** Iniciais de um nome, com no maximo 2 letras. */
export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/** Converte um texto em slug URL-safe. */
export function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/** Calcula offset/limit a partir de page/perPage. */
export function toPaginationRange(page?: number, perPage?: number) {
  const safePage = Math.max(1, page ?? PAGINATION.DEFAULT_PAGE);
  const safePerPage = Math.min(
    PAGINATION.MAX_PER_PAGE,
    Math.max(1, perPage ?? PAGINATION.DEFAULT_PER_PAGE),
  );

  return {
    page: safePage,
    perPage: safePerPage,
    skip: (safePage - 1) * safePerPage,
    take: safePerPage,
  };
}

/** Monta o envelope de paginacao usado pela API. */
export function buildPaginationMeta(total: number, page: number, perPage: number) {
  return {
    total,
    page,
    perPage,
    totalPages: Math.max(1, Math.ceil(total / perPage)),
  };
}

/** Promise que resolve apos `ms` milissegundos. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Remove chaves com valor `undefined` de um objeto. */
export function omitUndefined<T extends Record<string, unknown>>(input: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

// ---------------------------------------------------------------------------
// Datas da agenda
//
// Sem lib de data: sao poucas operacoes e todas em horario local, que e o que
// a agenda mostra. O fuso do servidor nunca entra aqui.
// ---------------------------------------------------------------------------

/**
 * A data no formato que `<input type="date">` entende. Le a data no fuso
 * local, e nao em UTC: `toISOString` devolveria o dia anterior para quem esta
 * a oeste de Greenwich, e o campo abriria sempre um dia atras.
 */
export function toDateInput(value: Date | string | null | undefined): string {
  if (!value) return '';

  const date = typeof value === 'string' ? new Date(value) : value;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * O caminho de volta: o texto do campo vira data no fuso local. `endOfDay`
 * serve ao fim de um periodo - sem ele, "ate 30/08" cortaria o proprio dia 30.
 */
export function fromDateInput(value: string, endOfDay = false): Date {
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`);
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours === 0) return `${rest} min`;

  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

export function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function addMonths(date: Date, months: number): Date {
  const copy = new Date(date);
  copy.setDate(1);
  copy.setMonth(copy.getMonth() + months);
  return copy;
}

/** Ultimo dia do mes da data - o que trava o dia 31 em fevereiro. */
export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/**
 * Vencimentos previstos de uma conta cadastrada dentro de um intervalo.
 *
 * O dia do vencimento e o do primeiro vencimento; em mes que nao tem esse dia
 * (31 em fevereiro) cai no ultimo dia do mes. Sem `endDate` a conta nao acaba -
 * quem limita e o `to` de quem pergunta.
 */
export function financeOccurrences(
  bill: {
    startDate: Date | string;
    endDate?: Date | string | null;
    recurrence: FinanceRecurrenceValue;
  },
  from: Date,
  to: Date,
): Date[] {
  const start = startOfDay(new Date(bill.startDate));
  const end = bill.endDate ? startOfDay(new Date(bill.endDate)) : null;
  const last = end && end.getTime() < to.getTime() ? end : startOfDay(to);
  const first = startOfDay(from);

  if (bill.recurrence === 'NONE') {
    return start.getTime() >= first.getTime() && start.getTime() <= last.getTime() ? [start] : [];
  }

  const dates: Date[] = [];
  const day = start.getDate();
  const monthStep = FINANCE_RECURRENCE_MONTHS[bill.recurrence];

  // Teto de seguranca: semanal por 10 anos nao passa disso.
  for (let index = 0; index < 600; index += 1) {
    const date =
      monthStep === 0
        ? addDays(start, index * 7)
        : (() => {
            const month = new Date(start.getFullYear(), start.getMonth() + index * monthStep, 1);
            return new Date(
              month.getFullYear(),
              month.getMonth(),
              Math.min(day, endOfMonth(month).getDate()),
            );
          })();

    if (date.getTime() > last.getTime()) break;
    if (date.getTime() >= first.getTime()) dates.push(date);
  }

  return dates;
}

export interface ReceivablePlanItem {
  installment: number;
  installments: number;
  grossAmount: number;
  feeAmount: number;
  netAmount: number;
  dueDate: Date;
}

/** Centavos, sempre - dinheiro nao tem terceira casa. */
function toCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * As parcelas que uma forma de pagamento gera: quanto cai, quanto a
 * maquininha fica e em que dia.
 *
 * A sobra dos centavos vai na primeira parcela - e a convencao do mercado e
 * evita que a soma feche com um centavo a menos. A primeira cai em
 * `daysToReceive` dias; as seguintes, de mes em mes.
 */
export function planReceivables(params: {
  amount: number;
  installments: number;
  feePercent: number;
  daysToReceive: number;
  paidAt: Date;
}): ReceivablePlanItem[] {
  const installments = Math.max(1, Math.trunc(params.installments));
  const total = toCents(params.amount);

  const base = Math.floor((total / installments) * 100) / 100;
  const remainder = toCents(total - base * installments);

  const first = startOfDay(addDays(params.paidAt, params.daysToReceive));

  return Array.from({ length: installments }, (_, index) => {
    const gross = toCents(index === 0 ? base + remainder : base);
    const fee = toCents((gross * params.feePercent) / 100);

    const month = new Date(first.getFullYear(), first.getMonth() + index, 1);
    const dueDate =
      index === 0
        ? first
        : new Date(
            month.getFullYear(),
            month.getMonth(),
            Math.min(first.getDate(), endOfMonth(month).getDate()),
          );

    return {
      installment: index + 1,
      installments,
      grossAmount: gross,
      feeAmount: fee,
      netAmount: toCents(gross - fee),
      dueDate,
    };
  });
}

/** Domingo como primeiro dia, que e o padrao dos calendarios em pt-BR. */
export function startOfWeek(date: Date): Date {
  return startOfDay(addDays(date, -date.getDay()));
}

export function startOfMonth(date: Date): Date {
  const copy = startOfDay(date);
  copy.setDate(1);
  return copy;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Intervalo [inicio, fim] que a agenda pede a API em cada visao. */
export function rangeForView(view: 'day' | 'week' | 'month' | 'schedule', reference: Date) {
  // A "programacao" lista o que vem pela frente, como no Google Agenda.
  if (view === 'schedule') {
    return { from: startOfDay(reference), to: endOfDay(addDays(reference, 30)) };
  }

  if (view === 'day') {
    return { from: startOfDay(reference), to: endOfDay(reference) };
  }

  if (view === 'week') {
    const from = startOfWeek(reference);
    return { from, to: endOfDay(addDays(from, 6)) };
  }

  // O mes na grade comeca no domingo da primeira semana e fecha 6 semanas
  // depois, para a grade nunca mudar de altura.
  const from = startOfWeek(startOfMonth(reference));
  return { from, to: endOfDay(addDays(from, 41)) };
}

/** Os 42 dias da grade mensal. */
export function monthGrid(reference: Date): Date[] {
  const start = startOfWeek(startOfMonth(reference));
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

/** Formata so o horario (HH:mm). */
export function formatTime(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
}
